import React, { useEffect, useState } from "react";
import "../../styles/clients.css";
import { Plus, Edit, Trash2, RefreshCw, X, Download, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { useGeneric } from "../../hooks/useGeneric";
import { confirmAndDelete } from "../../utils/deleteHelper";
import { vbDecrypt } from "../../utils/functions";
import { supabase } from "../../utils/supabaseClient";
import { matchesAgency } from "../../utils/agencySearch";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const EMPTY_FORM = {
    agency_id: null, agency_name: "", software_key: "", email_id: "",
    address1: "", address2: "", address3: "", phone: "", mobile: "", gst_no: "",
    is_rate_inclusive: "", is_mobile_approved: false, mobile_valid_upto: "",
    is_ewb_approved: false, is_e_invoice_approved: false, is_gst_approved: false,
    is_sync_approved: false, sync_valid_upto: "", amc_valid_upto: "", place: "",
    pincode: "", client_nos: 0, amc_amt: 0, api_balance: 0, whatsapp_balance: 0,
    installed_on: "", activated_on: "", last_updated: "", last_opened: "",
    owner_id: "", partner_id: "",
};

const Clients = () => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [showForm, setShowForm] = useState(false);
    const [errors, setErrors] = useState({});
    const [search, setSearch] = useState("");
    const [saving, setSaving] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [importText, setImportText] = useState("");
    const [importError, setImportError] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const { data: agencies = [], loading, fetchAll: fetchAgencies, save: saveAgency, remove: deleteAgency } = useGeneric({
        tableName: "agencies", idColumn: "agency_id", orderBy: "agency_name", removeOnInsert: ["agency_id"],
    });
    const { data: partners = [], fetchAll: fetchPartners } = useGeneric({
        tableName: "partners", idColumn: "partner_id", orderBy: "partner_name",
    });
    const { data: owners = [], fetchAll: fetchOwners } = useGeneric({
        tableName: "owners", idColumn: "owner_id", orderBy: "owner_name",
    });

    useEffect(() => { fetchAgencies(); fetchOwners(); fetchPartners(); }, [fetchAgencies, fetchOwners, fetchPartners]);
    useEffect(() => { setCurrentPage(1); }, [search, pageSize]);

    const set = (key, val) => {
        setForm((prev) => ({ ...prev, [key]: val }));
        setErrors((prev) => ({ ...prev, [key]: "" }));
    };

    const validate = () => {
        const errs = {};
        if (!form.agency_name.trim()) errs.agency_name = "Client name is required.";
        else if (form.agency_name.length > 50) errs.agency_name = "Max 50 characters.";
        if (!form.software_key?.trim()) errs.software_key = "Software Key is required.";
        if (!form.owner_id || form.owner_id === "" || form.owner_id === 0) errs.owner_id = "Owner is required.";
        if (!form.partner_id || form.partner_id === "" || form.partner_id === 0) errs.partner_id = "Partner is required.";
        if (!form.address1?.trim() && !form.address2?.trim() && !form.address3?.trim()) errs.address1 = "At least one address line is required.";
        if (!form.place?.trim()) errs.place = "Place is required.";
        const pincode = String(form.pincode ?? "").trim();
        if (!pincode) errs.pincode = "Pincode is required.";
        else if (pincode.length !== 6) errs.pincode = "Pincode must be 6 digits.";
        if (!form.phone?.trim() && !form.mobile?.trim()) errs.phone = "Phone or Mobile is required.";
        if (form.email_id && !/^\S+@\S+\.\S+$/.test(form.email_id)) errs.email_id = "Invalid email format.";
        if (!form.amc_valid_upto) errs.amc_valid_upto = "AMC date is required.";
        else if (isNaN(new Date(form.amc_valid_upto).getTime())) errs.amc_valid_upto = "Invalid AMC date.";
        if (!form.installed_on) errs.installed_on = "Installed date is required.";
        else if (isNaN(new Date(form.installed_on).getTime())) errs.installed_on = "Invalid installed date.";
        if (!form.activated_on) errs.activated_on = "Activated date is required.";
        else if (isNaN(new Date(form.activated_on).getTime())) errs.activated_on = "Invalid activated date.";
        if (form.client_nos && Number(form.client_nos) > 32767) errs.client_nos = "Must be within smallint range.";
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const onSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const cleaned = {
                ...form,
                mobile_valid_upto: form.mobile_valid_upto || null, sync_valid_upto: form.sync_valid_upto || null,
                last_updated: form.last_updated || null, last_opened: form.last_opened || null,
                amc_valid_upto: form.amc_valid_upto || null, installed_on: form.installed_on || null,
                activated_on: form.activated_on || null,
                client_nos: form.client_nos !== "" ? form.client_nos : null,
                amc_amt: form.amc_amt !== "" ? form.amc_amt : null,
                api_balance: form.api_balance !== "" ? form.api_balance : null,
            };
            await saveAgency(cleaned);
            closeModal();
        } catch (err) {
            alert("Save failed: " + (err.message || "Unknown error"));
        } finally {
            setSaving(false);
        }
    };

    const openAdd = () => { setForm(EMPTY_FORM); setErrors({}); setShowForm(true); };

    const openEdit = (agency) => {
        const toDate = (val) => val ? new Date(val).toISOString().split("T")[0] : "";
        setForm({
            ...agency,
            pincode: String(agency.pincode ?? ""), client_nos: String(agency.client_nos ?? 0),
            amc_amt: String(agency.amc_amt ?? 0), api_balance: String(agency.api_balance ?? 0),
            whatsapp_balance: String(agency.whatsapp_balance ?? 0),
            installed_on: toDate(agency.installed_on), activated_on: toDate(agency.activated_on),
            amc_valid_upto: toDate(agency.amc_valid_upto), mobile_valid_upto: toDate(agency.mobile_valid_upto),
            sync_valid_upto: toDate(agency.sync_valid_upto),
        });
        setErrors({});
        setShowForm(true);
    };

    const closeModal = () => { setShowForm(false); setForm(EMPTY_FORM); setErrors({}); };

    const handleDelete = (agency, e) => {
        e.stopPropagation(); // prevent row click from firing
        confirmAndDelete({
            what: "client",
            name: agency.agency_name,
            usedIn: "users, leads or service calls",
            run: () => deleteAgency(agency.agency_id),
        });
    };

    const handleRowClick = (agency) => { openEdit(agency); };

    const handleImport = async () => {
        setImportError("");
        if (!importText.trim()) { setImportError("Please paste the encrypted text."); return; }
        try {
            const decrypted = await vbDecrypt(importText.trim());
            if (!decrypted) { setImportError("Decryption failed. Please check the pasted text."); return; }
            const agency = JSON.parse(decrypted);
            const toDate = (val) => {
                if (!val) return null;
                const d = new Date(val);
                if (isNaN(d.getTime())) return null;
                if (d.getFullYear() < 1900) return null;
                return d.toISOString().split("T")[0];
            };
            const ownerMobile = agency.owner_mobile?.trim();
            if (!ownerMobile) { setImportError("Owner mobile is missing in the imported data."); return; }
            let ownerId = null;
            const { data: existingOwners, error: ownerSearchErr } = await supabase
                .from("owners").select("owner_id, owner_name").eq("mobile", ownerMobile).limit(1);
            if (ownerSearchErr) throw new Error("Owner search failed: " + ownerSearchErr.message);
            if (existingOwners && existingOwners.length > 0) {
                ownerId = existingOwners[0].owner_id;
            } else {
                if (!agency.owner_name?.trim()) { setImportError("Owner name is missing in the imported data."); return; }
                const { data: newOwner, error: ownerInsertErr } = await supabase
                    .from("owners").insert({
                        owner_name: agency.owner_name?.trim() ?? "", mobile: ownerMobile,
                        email: agency.owner_email?.trim() ?? "", designation: agency.owner_designation?.trim() ?? "",
                    }).select("owner_id").single();
                if (ownerInsertErr) throw new Error("Owner insert failed: " + ownerInsertErr.message);
                ownerId = newOwner.owner_id;
                await fetchOwners();
            }
            const partnerMobile = agency.partner_mobile?.trim();
            if (!partnerMobile) { setImportError("Partner mobile is missing in the imported data."); return; }
            const { data: existingPartners, error: partnerSearchErr } = await supabase
                .from("partners").select("partner_id, partner_name").eq("mobile", partnerMobile).limit(1);
            if (partnerSearchErr) throw new Error("Partner search failed: " + partnerSearchErr.message);
            if (!existingPartners || existingPartners.length === 0) {
                setImportError(`Partner with mobile ${partnerMobile} not found. Please add the partner first and try again.`);
                return;
            }
            const partnerId = existingPartners[0].partner_id;
            await fetchPartners();
            setForm({
                agency_id: agency.agency_id ?? null, agency_name: agency.agency_name ?? "",
                software_key: agency.software_key ?? "", email_id: agency.email_id ?? "",
                address1: agency.address1 ?? "", address2: agency.address2 ?? "", address3: agency.address3 ?? "",
                phone: agency.phone ?? "", mobile: agency.mobile ?? "", gst_no: agency.gst_no ?? "",
                is_rate_inclusive: agency.is_rate_inclusive ?? "", is_mobile_approved: agency.is_mobile_approved ?? false,
                mobile_valid_upto: toDate(agency.mobile_valid_upto), is_ewb_approved: agency.is_ewb_approved ?? false,
                is_e_invoice_approved: agency.is_e_invoice_approved ?? false, is_gst_approved: agency.is_gst_approved ?? false,
                is_sync_approved: agency.is_sync_approved ?? false, sync_valid_upto: toDate(agency.sync_valid_upto),
                amc_valid_upto: toDate(agency.amc_valid_upto), place: agency.place ?? "",
                pincode: String(agency.pincode ?? ""), client_nos: String(agency.client_nos ?? 0),
                amc_amt: String(agency.amc_amt ?? 0), api_balance: String(agency.api_balance ?? 0),
                whatsapp_balance: String(agency.whatsapp_balance ?? 0), installed_on: toDate(agency.installed_on),
                activated_on: toDate(agency.activated_on), last_updated: toDate(agency.last_updated),
                last_opened: toDate(agency.last_opened), owner_id: ownerId, partner_id: partnerId,
            });
            setErrors({}); setShowImport(false); setImportText(""); setShowForm(true);
        } catch (err) {
            setImportError("Error: " + (err.message || "Unknown error"));
        }
    };

    // ── Filter & Paginate ────────────────────────────────────────────
    const filtered = (agencies || []).filter((a) => matchesAgency(a, search, ["place"]));

    const totalRecords = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
    const safePage = Math.min(currentPage, totalPages);
    const startIdx = (safePage - 1) * pageSize;
    const paginated = filtered.slice(startIdx, startIdx + pageSize);

    const goTo = (page) => setCurrentPage(Math.max(1, Math.min(page, totalPages)));

    const getPageNumbers = () => {
        const pages = [];
        const left = Math.max(1, safePage - 2);
        const right = Math.min(totalPages, safePage + 2);
        for (let i = left; i <= right; i++) pages.push(i);
        return pages;
    };

    return (
        <div className="cl-page">
            <div className="cl-panel">
                {/* ── Heading ── */}
                <div className="cl-panel-heading">
                    <span>Client Details</span>
                    <div style={{ display: "flex", gap: "6px" }}>
                        <button className="cl-add-btn" onClick={() => { setImportText(""); setImportError(""); setShowImport(true); }} title="Import" style={{ background: "#6c757d" }}>
                            <Download size={16} />
                        </button>
                        <button className="cl-add-btn" onClick={openAdd} title="Add Client">
                            <Plus size={16} />
                        </button>
                    </div>
                </div>

                {/* ── Search bar ── */}
                <div className="cl-search-bar">
                    <input type="text" placeholder="Search by name, software ID, phone, mobile…" value={search}
                        onChange={(e) => setSearch(e.target.value)} className="cl-search-input" />
                    <button className="cl-refresh-btn" onClick={fetchAgencies} title="Refresh">
                        <RefreshCw size={15} />
                    </button>
                </div>

                {/* ── Table ── */}
                {loading ? (
                    <div className="cl-loading">Loading…</div>
                ) : (
                    <>
                        <div className="cl-table-wrapper">
                            <table className="cl-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Client ID</th>
                                        <th>Client Name</th>
                                        <th>Key</th>
                                        <th>Phone</th>
                                        <th className="cl-actions-th">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="cl-empty">No clients found.</td>
                                        </tr>
                                    ) : (
                                        paginated.map((a, i) => (
                                            <tr
                                                key={a.agency_id ?? i}
                                                className={`${i % 2 === 0 ? "cl-row-even" : "cl-row-odd"} cl-row-clickable`}
                                                onClick={() => handleRowClick(a)}
                                                title="Click to edit"
                                            >
                                                <td data-label="#">{startIdx + i + 1}</td>
                                                <td data-label="Client ID">{a.agency_id}</td>
                                                <td data-label="Client Name">{a.agency_name}</td>
                                                <td data-label="Key">{a.software_key}</td>
                                                <td data-label="Phone">{a.phone}</td>
                                                <td className="cl-actions-td" onClick={(e) => e.stopPropagation()}>
                                                    <button className="cl-icon-btn cl-edit" onClick={() => openEdit(a)} title="Edit">
                                                        <Edit size={15} /><span className="cl-icon-label">Edit</span>
                                                    </button>
                                                    <button className="cl-icon-btn cl-delete" onClick={(e) => handleDelete(a, e)} title="Delete">
                                                        <Trash2 size={15} /><span className="cl-icon-label">Delete</span>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* ── Pagination bar ── */}
                        <div className="cl-pagination">
                            <div className="cl-pagination-info">
                                {totalRecords === 0 ? "No records" : `Showing ${startIdx + 1}–${Math.min(startIdx + pageSize, totalRecords)} of ${totalRecords}`}
                            </div>

                            <div className="cl-pagination-controls">
                                <button className="cl-page-btn" onClick={() => goTo(1)} disabled={safePage === 1} title="First">
                                    <ChevronsLeft size={15} />
                                </button>
                                <button className="cl-page-btn" onClick={() => goTo(safePage - 1)} disabled={safePage === 1} title="Previous">
                                    <ChevronLeft size={15} />
                                </button>

                                {getPageNumbers()[0] > 1 && (
                                    <><button className="cl-page-btn" onClick={() => goTo(1)}>1</button>
                                        {getPageNumbers()[0] > 2 && <span className="cl-page-ellipsis">…</span>}</>
                                )}

                                {getPageNumbers().map((p) => (
                                    <button key={p} className={`cl-page-btn ${p === safePage ? "cl-page-active" : ""}`} onClick={() => goTo(p)}>
                                        {p}
                                    </button>
                                ))}

                                {getPageNumbers()[getPageNumbers().length - 1] < totalPages && (
                                    <>{getPageNumbers()[getPageNumbers().length - 1] < totalPages - 1 && <span className="cl-page-ellipsis">…</span>}
                                        <button className="cl-page-btn" onClick={() => goTo(totalPages)}>{totalPages}</button></>
                                )}

                                <button className="cl-page-btn" onClick={() => goTo(safePage + 1)} disabled={safePage === totalPages} title="Next">
                                    <ChevronRight size={15} />
                                </button>
                                <button className="cl-page-btn" onClick={() => goTo(totalPages)} disabled={safePage === totalPages} title="Last">
                                    <ChevronsRight size={15} />
                                </button>
                            </div>

                            <div className="cl-pagination-size">
                                <label>Rows:</label>
                                <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                                    {PAGE_SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        </div>
                    </>
                )}

                <div className="cl-panel-footer" />
            </div>

            {/* ── Edit/Add Modal ── */}
            {showForm && (
                <div className="cl-modal-backdrop">
                    <div className="cl-modal-overlay" />
                    <div className="cl-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="cl-modal-header">
                            <h4 className="cl-modal-title">{form.agency_id ? "Edit Client" : "Add Client"}</h4>
                            <button className="cl-modal-close" onClick={closeModal}><X size={18} /></button>
                        </div>
                        <div className="cl-modal-body">
                            <form autoComplete="off">
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-10">
                                        <label className="cl-control-label">Client Name</label>
                                        <input type="text" className={`cl-form-control${errors.agency_name ? " cl-input-error" : ""}`} placeholder="Agency Name" maxLength={50} value={form.agency_name} onChange={(e) => set("agency_name", e.target.value)} />
                                        {errors.agency_name && <span className="cl-error-msg">{errors.agency_name}</span>}
                                    </div>
                                    <div className="cl-col-2">
                                        <label className="cl-control-label">Client ID</label>
                                        <input type="text" className="cl-form-control cl-readonly" placeholder="Auto" value={form.agency_id ?? ""} readOnly />
                                    </div>
                                </div>
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-12">
                                        <label className="cl-control-label">Software Key</label>
                                        <input type="text" className={`cl-form-control${errors.software_key ? " cl-input-error" : ""}`} placeholder="Software Key" maxLength={50} value={form.software_key} onChange={(e) => set("software_key", e.target.value)} />
                                        {errors.software_key && <span className="cl-error-msg">{errors.software_key}</span>}
                                    </div>
                                </div>
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-6">
                                        <label className="cl-control-label">Owner</label>
                                        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                                            <select className={`cl-form-control cl-select${errors.owner_id ? " cl-input-error" : ""}`} style={{ flex: 1 }} value={form.owner_id ?? ""} onChange={(e) => set("owner_id", e.target.value)}>
                                                <option value="">— Select Owner —</option>
                                                {owners.map((o, i) => <option key={o.owner_id ?? i} value={o.owner_id}>{o.owner_name}</option>)}
                                            </select>
                                            <button type="button" className="cl-refresh-icon-btn" onClick={fetchOwners} title="Refresh Owners"><RefreshCw size={14} /></button>
                                        </div>
                                        {errors.owner_id && <span className="cl-error-msg">{errors.owner_id}</span>}
                                    </div>
                                    <div className="cl-col-6">
                                        <label className="cl-control-label">Partner</label>
                                        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                                            <select className={`cl-form-control cl-select${errors.partner_id ? " cl-input-error" : ""}`} style={{ flex: 1 }} value={form.partner_id ?? ""} onChange={(e) => set("partner_id", e.target.value)}>
                                                <option value="">— Select Partner —</option>
                                                {partners.map((p, i) => <option key={p.partner_id ?? i} value={p.partner_id}>{p.partner_name}</option>)}
                                            </select>
                                            <button type="button" className="cl-refresh-icon-btn" onClick={fetchPartners} title="Refresh Partners"><RefreshCw size={14} /></button>
                                        </div>
                                        {errors.partner_id && <span className="cl-error-msg">{errors.partner_id}</span>}
                                    </div>
                                </div>
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-4">
                                        <label className="cl-control-label">Address 1</label>
                                        <input type="text" className={`cl-form-control${errors.address1 ? " cl-input-error" : ""}`} placeholder="Address 1" maxLength={50} value={form.address1} onChange={(e) => set("address1", e.target.value)} />
                                        {errors.address1 && <span className="cl-error-msg">{errors.address1}</span>}
                                    </div>
                                    <div className="cl-col-4">
                                        <label className="cl-control-label">Address 2</label>
                                        <input type="text" className="cl-form-control" placeholder="Address 2" maxLength={50} value={form.address2} onChange={(e) => set("address2", e.target.value)} />
                                    </div>
                                    <div className="cl-col-4">
                                        <label className="cl-control-label">Address 3</label>
                                        <input type="text" className="cl-form-control" placeholder="Address 3" maxLength={50} value={form.address3} onChange={(e) => set("address3", e.target.value)} />
                                    </div>
                                </div>
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-4">
                                        <label className="cl-control-label">GST No.</label>
                                        <input type="text" className="cl-form-control" placeholder="GST No." maxLength={50} value={form.gst_no} onChange={(e) => set("gst_no", e.target.value)} />
                                    </div>
                                    <div className="cl-col-4">
                                        <label className="cl-control-label">Place</label>
                                        <input type="text" className={`cl-form-control${errors.place ? " cl-input-error" : ""}`} placeholder="Place" maxLength={50} value={form.place} onChange={(e) => set("place", e.target.value)} />
                                        {errors.place && <span className="cl-error-msg">{errors.place}</span>}
                                    </div>
                                    <div className="cl-col-4">
                                        <label className="cl-control-label">Pincode</label>
                                        <input type="text" className={`cl-form-control${errors.pincode ? " cl-input-error" : ""}`} placeholder="Pincode" maxLength={6} value={form.pincode} onChange={(e) => set("pincode", e.target.value.replace(/\D/g, ""))} />
                                        {errors.pincode && <span className="cl-error-msg">{errors.pincode}</span>}
                                    </div>
                                </div>
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-4">
                                        <label className="cl-control-label">Phone</label>
                                        <input type="text" className={`cl-form-control${errors.phone ? " cl-input-error" : ""}`} placeholder="Phone" maxLength={20} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                                        {errors.phone && <span className="cl-error-msg">{errors.phone}</span>}
                                    </div>
                                    <div className="cl-col-4">
                                        <label className="cl-control-label">Mobile</label>
                                        <input type="text" className="cl-form-control" placeholder="Mobile" maxLength={20} value={form.mobile} onChange={(e) => set("mobile", e.target.value)} />
                                    </div>
                                    <div className="cl-col-4">
                                        <label className="cl-control-label">Email</label>
                                        <input type="email" className={`cl-form-control${errors.email_id ? " cl-input-error" : ""}`} placeholder="Email" maxLength={50} value={form.email_id} onChange={(e) => set("email_id", e.target.value)} />
                                        {errors.email_id && <span className="cl-error-msg">{errors.email_id}</span>}
                                    </div>
                                </div>
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-2">
                                        <label className="cl-control-label">Client Nos</label>
                                        <input type="number" className={`cl-form-control${errors.client_nos ? " cl-input-error" : ""}`} placeholder="Client Nos" value={form.client_nos ?? ""} onChange={(e) => set("client_nos", e.target.value)} />
                                        {errors.client_nos && <span className="cl-error-msg">{errors.client_nos}</span>}
                                    </div>
                                    <div className="cl-col-2">
                                        <label className="cl-control-label">AMC Amt</label>
                                        <input type="number" className="cl-form-control" placeholder="AMC Amt" value={form.amc_amt ?? ""} onChange={(e) => set("amc_amt", e.target.value)} />
                                    </div>
                                    <div className="cl-col-2">
                                        <label className="cl-control-label">API Balance</label>
                                        <input type="number" className="cl-form-control" placeholder="API Balance" value={form.api_balance ?? ""} onChange={(e) => set("api_balance", e.target.value)} />
                                    </div>
                                    <div className="cl-col-3">
                                        <label className="cl-control-label">Whatsapp Balance</label>
                                        <input type="number" className="cl-form-control" placeholder="Whatsapp Balance" value={form.whatsapp_balance ?? ""} onChange={(e) => set("whatsapp_balance", e.target.value)} />
                                    </div>
                                    <div className="cl-col-3">
                                        <label className="cl-control-label">AMC Date</label>
                                        <input type="date" className={`cl-form-control${errors.amc_valid_upto ? " cl-input-error" : ""}`} value={form.amc_valid_upto ?? ""} onChange={(e) => set("amc_valid_upto", e.target.value)} />
                                        {errors.amc_valid_upto && <span className="cl-error-msg">{errors.amc_valid_upto}</span>}
                                    </div>
                                </div>
                                <div className="cl-form-group cl-row" style={{ alignItems: "flex-start" }}>
                                    <div className="cl-col-3">
                                        <label className="cl-control-label">Installed Date</label>
                                        <input type="date" className={`cl-form-control${errors.installed_on ? " cl-input-error" : ""}`} value={form.installed_on ?? ""} onChange={(e) => set("installed_on", e.target.value)} />
                                        {errors.installed_on && <span className="cl-error-msg">{errors.installed_on}</span>}
                                    </div>
                                    <div className="cl-col-3">
                                        <label className="cl-control-label">Activated Date</label>
                                        <input type="date" className={`cl-form-control${errors.activated_on ? " cl-input-error" : ""}`} value={form.activated_on ?? ""} onChange={(e) => set("activated_on", e.target.value)} />
                                        {errors.activated_on && <span className="cl-error-msg">{errors.activated_on}</span>}
                                    </div>
                                    <div className="cl-col-3">
                                        <label className="cl-checkbox-label">
                                            <input type="checkbox" className="cl-checkbox" checked={!!form.is_mobile_approved} onChange={(e) => set("is_mobile_approved", e.target.checked)} />
                                            Is Mobile
                                        </label>
                                        <input type="date" className="cl-form-control" value={form.mobile_valid_upto ?? ""} disabled={!form.is_mobile_approved} onChange={(e) => set("mobile_valid_upto", e.target.value)} />
                                    </div>
                                    <div className="cl-col-3">
                                        <label className="cl-checkbox-label">
                                            <input type="checkbox" className="cl-checkbox" checked={!!form.is_sync_approved} onChange={(e) => set("is_sync_approved", e.target.checked)} />
                                            Is Synchronise
                                        </label>
                                        <input type="date" className="cl-form-control" value={form.sync_valid_upto ?? ""} disabled={!form.is_sync_approved} onChange={(e) => set("sync_valid_upto", e.target.value)} />
                                    </div>
                                </div>
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-flag">
                                        <label className="cl-checkbox-label">
                                            <input type="checkbox" className="cl-checkbox" checked={!!form.is_ewb_approved} onChange={(e) => set("is_ewb_approved", e.target.checked)} />
                                            Is eWay Bill
                                        </label>
                                    </div>
                                    <div className="cl-col-flag">
                                        <label className="cl-checkbox-label">
                                            <input type="checkbox" className="cl-checkbox" checked={!!form.is_e_invoice_approved} onChange={(e) => set("is_e_invoice_approved", e.target.checked)} />
                                            Is eInvoice
                                        </label>
                                    </div>
                                    <div className="cl-col-flag">
                                        <label className="cl-checkbox-label">
                                            <input type="checkbox" className="cl-checkbox" checked={!!form.is_gst_approved} onChange={(e) => set("is_gst_approved", e.target.checked)} />
                                            Is GST
                                        </label>
                                    </div>
                                </div>
                            </form>
                        </div>
                        <div className="cl-modal-footer">
                            <button className="cl-btn cl-btn-default" onClick={closeModal}>Close</button>
                            <button className="cl-btn cl-btn-primary" onClick={onSave} disabled={saving}>
                                {saving ? "Saving…" : form.agency_id ? "Update" : "Add"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Import Modal ── */}
            {showImport && (
                <div className="cl-modal-backdrop">
                    <div className="cl-modal-overlay" />
                    <div className="cl-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "500px" }}>
                        <div className="cl-modal-header">
                            <h4 className="cl-modal-title">Import Client Data</h4>
                            <button className="cl-modal-close" onClick={() => setShowImport(false)}><X size={18} /></button>
                        </div>
                        <div className="cl-modal-body">
                            <label className="cl-control-label">Paste the encrypted text received from client site:</label>
                            <textarea rows={16} className="cl-form-control"
                                style={{ marginTop: "8px", resize: "vertical", fontFamily: "monospace", fontSize: "12px", height: "300px", minHeight: "300px" }}
                                placeholder="Paste encrypted text here…" value={importText}
                                onChange={(e) => { setImportText(e.target.value); setImportError(""); }} />
                            {importError && <span className="cl-error-msg" style={{ marginTop: "6px", display: "block" }}>{importError}</span>}
                        </div>
                        <div className="cl-modal-footer">
                            <button className="cl-btn cl-btn-default" onClick={() => setShowImport(false)}>Cancel</button>
                            <button className="cl-btn cl-btn-primary" onClick={handleImport}>Decrypt & Load</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Clients;
